import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queueApi } from "@bw/shared/api";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Leave a party (DELETE /queue/party/{partyId}). The server transfers an open
 * party to the next available member when its leader leaves, and disbands only
 * when no available member remains.
 *
 * The acting member is the token user (server-derived). On settlement the party
 * detail and facility-scoped inventory refetch, including after stale rejection. Errors are NOT
 * swallowed — a 403 (not in the party) or a queued-party ValueError surfaces on
 * `mutation.error` for the page to map.
 */
export function useLeaveParty(facilityId?: string) {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, string>({
    mutationFn: (partyId: string) => queueApi.leaveParty(partyId),
    onSettled: (_data, _error, partyId) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.party.detail(partyId),
        refetchType: "active",
      });
      if (!facilityId) return;
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.myParty(facilityId),
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.partyMemberships(facilityId),
        refetchType: "active",
      });
    },
  });
}
